/**
 * May this tab adopt the version a server write returned as its save token?
 *
 * `projects.updated_at` is the save token: every full save presents it as
 * expectedUpdatedAt and the server refuses a spent one. Several writes from
 * this tab move the row without going through the full save (the narrow
 * lifecycle routes, the Quote # PATCH, the Work Method partial save). Each
 * hands back the row's new version, and adopting it keeps this tab's next
 * save from being refused for a change it made itself.
 *
 * Adopting is only safe when the write landed on the version this tab holds.
 * If another device saved in between, the returned version already contains
 * that save while this tab's scope and form data do not. Adopting it would let
 * the tab's next full save pass the server's stale-write check and overwrite
 * the other device's work, and the remote-version monitor would read the new
 * version as this tab's own and never report the other save. Keeping the held
 * token instead leaves the conflict visible: the monitor reports the newer
 * version and the next save is refused for review.
 *
 * The proof comes from one of two places:
 *   - `writtenOver`: the token a gated write presented (the Work Method
 *     partial save sends this tab's token as expectedUpdatedAt, so the server
 *     refused it unless the row was at that version). Adopt while the tab
 *     still holds that same token.
 *   - `previousUpdatedAt` on the response: the narrow routes read the version
 *     they replace under the row lock (backend/src/routes/projects.js,
 *     lockProjectVersionForNarrowWrite). Adopt when it equals the held token.
 * A response with neither keeps the earlier behaviour: those callers adopt a
 * full row they re-read after a write gated on its own token.
 *
 * A matching proof is not enough when the same job was reloaded during the
 * write. The reload can read the row from before the write committed, so the
 * tab holds the replaced version again while showing content without the
 * write. Adopting then would let the next autosave put that content back with
 * no conflict. A caller that names the load generation its write started on
 * (`loadGeneration`) is adopted only while that load is still the one open
 * (`openLoadGeneration`).
 *
 * Returns the token to adopt, or null to keep the one the tab holds.
 */
export interface ServerVersionAdoptionInput {
    /** The project the write was for. */
    projectId: unknown;
    /** The project open in this tab. */
    openProjectId: unknown;
    /** The remote-version monitor has latched a newer version for review. */
    remoteUpdateBlocked: boolean;
    /** The token this tab holds now (baseUpdatedAtRef). */
    heldToken: string | null;
    /** The row the write returned. */
    persisted: unknown;
    /** The token a gated write presented as expectedUpdatedAt, when it was gated. */
    writtenOver?: string | null;
    /** The tab's load generation when the write started. Leave it out when the caller does not track it. */
    loadGeneration?: number | null;
    /** The tab's load generation now (projectLoadGenerationRef). */
    openLoadGeneration?: number | null;
}

function instant(raw: unknown): number | null {
    if (!raw || (typeof raw !== 'string' && !(raw instanceof Date))) return null;
    const ms = new Date(raw as string).getTime();
    return Number.isNaN(ms) ? null : ms;
}

function versionOf(record: unknown): string | null {
    if (!record || typeof record !== 'object') return null;
    const row = record as Record<string, unknown>;
    const raw = row.updatedAt || row.updated_at || null;
    if (!raw) return null;
    const parsed = new Date(raw as string);
    return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
}

/** The version the write replaced, or undefined when the response does not say. */
function proofOf(persisted: unknown, writtenOver: string | null | undefined): unknown {
    if (writtenOver !== undefined) return writtenOver;
    if (!persisted || typeof persisted !== 'object') return undefined;
    const row = persisted as Record<string, unknown>;
    if (Object.prototype.hasOwnProperty.call(row, 'previousUpdatedAt')) return row.previousUpdatedAt;
    if (Object.prototype.hasOwnProperty.call(row, 'previous_updated_at')) return row.previous_updated_at;
    return undefined;
}

export function serverVersionToAdopt({
    projectId,
    openProjectId,
    remoteUpdateBlocked,
    heldToken,
    persisted,
    writtenOver,
    loadGeneration,
    openLoadGeneration,
}: ServerVersionAdoptionInput): string | null {
    if (!projectId || projectId !== openProjectId) return null;
    if (loadGeneration !== undefined) {
        if (typeof loadGeneration !== 'number' || loadGeneration !== openLoadGeneration) return null;
    }
    // A narrow lifecycle response can carry a newer token without carrying
    // the newer Project body. Never let that token re-arm a stale full-row
    // editor to overwrite work the remote-version monitor already saw.
    if (remoteUpdateBlocked) return null;
    const proof = proofOf(persisted, writtenOver);
    if (proof !== undefined) {
        const replaced = instant(proof);
        const held = instant(heldToken);
        if (replaced === null || held === null || replaced !== held) return null;
    }
    return versionOf(persisted);
}
